import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import type { ChapterReorderPosition } from "../../../shared/chatChapterTools";

type Props = {
  open: boolean;
  sourceChapter: Chapter | null;
  chapters: Chapter[];
  onClose: () => void;
  onMove: (targetChapterId: string, position: ChapterReorderPosition) => void;
};

export function MoveChapterDialog({ open, sourceChapter, chapters, onClose, onMove }: Props) {
  const [targetChapterId, setTargetChapterId] = useState("");
  const [position, setPosition] = useState<ChapterReorderPosition>("before");

  const orderedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id)),
    [chapters]
  );

  const targetOptions = useMemo(
    () => orderedChapters.filter((chapter) => chapter.id !== sourceChapter?.id),
    [orderedChapters, sourceChapter?.id]
  );

  useEffect(() => {
    if (!open) return;
    setPosition("before");
    setTargetChapterId(targetOptions[0]?.id ?? "");
  }, [open, sourceChapter?.id, targetOptions]);

  const canMove = targetOptions.length > 0 && Boolean(targetChapterId);

  return (
    <Dialog
      open={open && Boolean(sourceChapter)}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton>
        <DialogHeader>
          <DialogTitle>Move chapter</DialogTitle>
          <DialogDescription>
            Move{" "}
            <strong>
              {sourceChapter?.order}. {sourceChapter?.title}
            </strong>{" "}
            relative to another chapter.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="move-chapter-position">Position</Label>
            <Select
              value={position}
              onValueChange={(value) => setPosition(value as ChapterReorderPosition)}
            >
              <SelectTrigger id="move-chapter-position" className="w-full" size="default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="before">Before</SelectItem>
                <SelectItem value="after">After</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="move-chapter-target">Reference chapter</Label>
            <Select
              value={targetChapterId || undefined}
              onValueChange={setTargetChapterId}
              disabled={targetOptions.length === 0}
            >
              <SelectTrigger id="move-chapter-target" className="w-full" size="default">
                <SelectValue placeholder="Select a chapter" />
              </SelectTrigger>
              <SelectContent>
                {targetOptions.map((chapter) => (
                  <SelectItem key={chapter.id} value={chapter.id}>
                    {chapter.order}. {chapter.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canMove}
            onClick={() => {
              if (!canMove) return;
              onMove(targetChapterId, position);
              onClose();
            }}
          >
            Move chapter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
